import { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import ConfirmationPopup from "../../components/ConfirmationPopup/ConfirmationPopup";
import IconButton from "../../components/IconButton/IconButton";
import PrimaryButton from "../../components/PrimaryButton/PrimaryButton";
import SecondaryButton from "../../components/SecondaryButton/SecondaryButton";
import PageWrapper from "../PageWrapper/PageWrapper";
import deleteIcon from "../../assets/images/bin.svg";
// import addMeetingIcon from "../../assets/images/add-meeting.svg";
import editIcon from "../../assets/images/pen.svg";
import lowPriorityIcon from '../../assets/images/warning-off.png';
import highPriorityIcon from '../../assets/images/warning.png';
import Card from "../../components/Card/Card";
import { Icon } from "../../components/Icon/Icon";
import { Modal } from "../../components/Modal/Modal";
import { FormType } from "../../shared/form.type";
import FilterPill from "../../components/FilterPill/FilterPill";
import CustomerForm from "../../components/CustomerForm/CustomerForm";
import CustomersAPI, { Customer, CustomerSortBy, CustomerSortDirection } from "../../api/CustomersAPI";
import CustomersCrmAutomationAPI, { CustomerCrmAutomationSettings } from "../../api/CustomersCrmAutomationAPI";
import { useGetCustomers } from "../../hooks/useGetCustomers";
import usePermissionFlag from "../../hooks/usePermissionFlag";
import EmployeesAPI, { Employee } from "../../api/EmployeesAPI";
import { CustomerStatus, customerStatusToTranslationKey, getCustomerStatuses } from "../../shared/customerStatusToTranslationKey";
import { Autocomplete } from "../../components/Autocomplete/Autocomplete";
import CustomerSearchResult from "../../components/CustomerSearchResult/CustomerSearchResult";
import { PermissionFlags } from "../../components/PermissionFlags/PermissionFlags";
import CustomerStatusPill from "../../components/CustomerStatusPill/CustomerStatusPill";
import { CancelToken } from "../../api/SimpleAPIClient";
import MeetingForm from "../../components/MeetingForm/MeetingForm";
import { Meeting } from "../../api/MeetingsAPI";
import NoDataCTA from "../../components/NoDataCTA/NoDataCTA";
import Table, { ColumnDefinition } from "../../components/Table/Table";
import { useNavigate } from "react-router-dom";
import funnelIcon from "../../assets/images/funnel.svg";
import xMarkIcon from "../../assets/images/x-mark.svg";
import Tooltip from "../../components/Tooltip/Tooltip";
import { parseDateTime, parseLegacyDateArray, safeParseDate } from "../../shared/timeUtils";
import DropSelect from "../../components/DropSelect/DropSelect";
import Switch from "../../components/Switch/Switch";
import { FileText, ArrowLeft, Plus } from "lucide-react";
import TryOnHeatBadge from "../../components/TryOnHeatBadge/TryOnHeatBadge";
import { countTryOnAppointments } from "../../shared/customerTryOnUtils";
import {
  CUSTOMER_FILTER_PRESET_IDS,
  CustomerFilterPresetId,
  customerFilterPresetTranslationKey,
  detectActivePreset,
  getCurrentMonthDropSelectValue,
  getPresetStatuses,
} from "../../shared/customerFilterPresets";
import {
  formatWeddingUrgency,
  weddingUrgencyToneClass,
} from "../../shared/customerWeddingUrgency";
import { toastService } from "../../services/ToastService";
import CustomerTableStatusSelect from "./CustomerTableStatusSelect";
import CustomerTableAssigneeSelect from "./CustomerTableAssigneeSelect";

/** Customer row shape for the table, including virtual column keys not returned by the API. */
type CustomerTableRow = Customer & { tryOnCount?: number };

export type CustomerColumn = ColumnDefinition<CustomerTableRow>;

type CustomerFilterState = {
  employeeIds: string[];
  statuses: CustomerStatus[];
  withoutUpcomingAppointment: boolean;
};

const mapCustomerSortColumn = (column: keyof CustomerTableRow | null): CustomerSortBy | undefined => {
  if (column === 'weddingDate') {
    return 'weddingDate';
  }

  if (column === 'meetings') {
    return 'nextAppointmentDate';
  }

  if (column === 'tryOnCount') {
    return 'tryOnCount';
  }

  return undefined;
};

const EMPTY_CUSSTOMER: Customer = {
  name: '',
  surname: '',
  countryCode: 'de',
  status: 'NEW',
  attachments: [],
  notes: '',
  flagged: false,
};

const EMPTY_MEETING: Meeting = {
  customerName: '',
  customerSurname: '',
  startTime: new Date().toISOString(),
  endTime: new Date().toISOString(),
  type: 'VISIT',
  reminder: false,
  disable: false,
  weddingDate: null,
  phone: null,
  lastUpdateTime: null,
  notes: null,
  title: null
};

const loadEnrichedCustomerOrFallback = async (customer: Customer): Promise<Customer> => {
  if (customer.id) {
    try {
      const response = await CustomersAPI.getById(customer.id);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch customer details:', error);
      return customer;
    }
  }
  return customer;
};

const getUpcomingAppointments = (customer: Customer): Meeting[] => {
  const meetings =
    customer.enrichedMeetings?.map((enrichedMeeting) => enrichedMeeting.meeting as Meeting) ||
    (customer.meetings as Meeting[] | undefined) ||
    [];
  const nowTs = Date.now();

  return meetings
    .filter((meeting): meeting is Meeting => {
      if (!meeting?.endTime) {
        return false;
      }

      const endTs = new Date(meeting.endTime).getTime();
      return !Number.isNaN(endTs) && endTs >= nowTs;
    })
    .sort((left, right) => {
      const leftStartTs = new Date(left.startTime).getTime();
      const rightStartTs = new Date(right.startTime).getTime();

      if (Number.isNaN(leftStartTs) && Number.isNaN(rightStartTs)) {
        return 0;
      }

      if (Number.isNaN(leftStartTs)) {
        return 1;
      }

      if (Number.isNaN(rightStartTs)) {
        return -1;
      }

      return leftStartTs - rightStartTs;
    });
};

const CustomersPage = () => {
  const [action, setAction] = useState<FormType>('READ');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [filters, setFilters] = useState<CustomerFilterState>({ employeeIds: [], statuses: [], withoutUpcomingAppointment: false });
  const [selectedWeddingMonthDate, setSelectedWeddingMonthDate] = useState('');
  const [sortBy, setSortBy] = useState<keyof CustomerTableRow | null>('meetings');
  const [sortDirection, setSortDirection] = useState<CustomerSortDirection>('ASC');
  const [activePresetId, setActivePresetId] = useState<CustomerFilterPresetId | null>(null);
  const [searchIsLoading, setSearchIsLoading] = useState(false);
  const [showMeetingForm, setShowMeetingForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showCrmAutomationSettingsView, setShowCrmAutomationSettingsView] = useState(false);
  const [crmAutomationSettings, setCrmAutomationSettings] = useState<CustomerCrmAutomationSettings | null>(null);
  const [crmAutomationLoading, setCrmAutomationLoading] = useState(false);
  const [crmAutomationSaveLoading, setCrmAutomationSaveLoading] = useState(false);
  const [crmAutomationMessage, setCrmAutomationMessage] = useState<string | null>(null);
  const [savingCustomerId, setSavingCustomerId] = useState<string | null>(null);
  const selectedWeddingMonth = useMemo(() => selectedWeddingMonthDate ? selectedWeddingMonthDate.substring(0, 7) : undefined, [selectedWeddingMonthDate]);
  const customerSortBy = mapCustomerSortColumn(sortBy);
  const customerQueryFilters = {
    ...filters,
    weddingMonth: selectedWeddingMonth,
    sortBy: customerSortBy,
    sortDirection: customerSortBy ? sortDirection : undefined,
  };
  const { isLoading, customers, pagination, addFn, updateFn, removeFn, setPage } = useGetCustomers(customerQueryFilters);
  const { hasAccess: canEditCustomers } = usePermissionFlag(['edit-customers']);

  const navigate = useNavigate(); 
  const { t } = useTranslation();

  const search = (input: string, resultsSize: number, setResultsFn: any) => {
    setSearchIsLoading(true);
    CustomersAPI.search(input).then((resp) => {
      const raw = resp.data.content;
      const respRaw = raw.slice(0, resultsSize);
      setSearchIsLoading(false);
      setResultsFn(respRaw);
    });
    return CancelToken.source().cancel("Cancelling...");
  };

  const load = async () => {
    const employeesResp = await EmployeesAPI.getSalesRepresentatives();
    setEmployees(employeesResp.data.map(s => s));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const employeeNameById = useMemo(() => {
    const map = new Map<string, string>();
    employees.forEach((employee) => {
      if (employee.id != null) {
        map.set(String(employee.id), employee.name);
      }
    });
    return map;
  }, [employees]);

  const resolvedActivePresetId = useMemo(
    () =>
      activePresetId ??
      detectActivePreset(filters.statuses, filters.employeeIds, selectedWeddingMonthDate, filters.withoutUpcomingAppointment),
    [activePresetId, filters.statuses, filters.employeeIds, selectedWeddingMonthDate, filters.withoutUpcomingAppointment],
  );

  useEffect(() => {
    if (!showCrmAutomationSettingsView || !canEditCustomers) {
      return;
    }
    let cancelled = false;
    const run = async () => {
      setCrmAutomationLoading(true);
      setCrmAutomationMessage(null);
      try {
        const res = await CustomersCrmAutomationAPI.get();
        if (!cancelled) {
          setCrmAutomationSettings(res.data);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setCrmAutomationMessage(t("customers.crm_automation.load_error"));
        }
      } finally {
        if (!cancelled) {
          setCrmAutomationLoading(false);
        }
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [showCrmAutomationSettingsView, canEditCustomers, t]);

  const clearPresetSelection = () => {
    setActivePresetId(null);
  };

  const applyPreset = (presetId: CustomerFilterPresetId) => {
    setActivePresetId(presetId);
    if (presetId === 'wedding_this_month') {
      setFilters({ employeeIds: [], statuses: [], withoutUpcomingAppointment: false });
      setSelectedWeddingMonthDate(getCurrentMonthDropSelectValue());
    } else {
      setFilters({
        employeeIds: [],
        statuses: getPresetStatuses(presetId),
        withoutUpcomingAppointment: presetId === 'no_next_appointment',
      });
      setSelectedWeddingMonthDate('');
    }
    setPage(0);
  };

  const toggleEmployee = (employee: Employee) => {
    clearPresetSelection();
    const employeeId = String(employee.id!);
    setFilters(prev => {
      const employeeIds = prev.employeeIds.includes(employeeId)
        ? prev.employeeIds.filter(id => id !== employeeId)
        : [...prev.employeeIds, employeeId];
      return { ...prev, employeeIds, withoutUpcomingAppointment: false };
    });
    setPage(0);
  };

  const toggleStatus = (status: CustomerStatus) => {
    clearPresetSelection();
    setFilters(prev => {
      const statuses = prev.statuses.includes(status)
        ? prev.statuses.filter(selectedStatus => selectedStatus !== status)
        : [...prev.statuses, status];
      return { ...prev, statuses, withoutUpcomingAppointment: false };
    });
    setPage(0);
  }

  const handleWeddingMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    clearPresetSelection();
    setSelectedWeddingMonthDate(e.target.value || '');
    setFilters(prev => ({ ...prev, withoutUpcomingAppointment: false }));
    setPage(0);
  };

  const handleSort = (column: keyof CustomerTableRow) => {
    if (sortBy === column) {
      setSortDirection(prev => prev === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(column);
      setSortDirection('ASC');
    }
    setPage(0);
  };

  const resetFilters = () => {
    setActivePresetId(null);
    setFilters({ employeeIds: [], statuses: [], withoutUpcomingAppointment: false });
    setSelectedWeddingMonthDate('');
    setPage(0);
  };

  const patchCrmAutomation = (partial: Partial<CustomerCrmAutomationSettings>) => {
    setCrmAutomationSettings((prev) => (prev ? { ...prev, ...partial } : prev));
  };

  const saveCrmAutomationSettings = async () => {
    if (!crmAutomationSettings) {
      return;
    }
    setCrmAutomationSaveLoading(true);
    setCrmAutomationMessage(null);
    try {
      const res = await CustomersCrmAutomationAPI.save(crmAutomationSettings);
      setCrmAutomationSettings(res.data);
      setCrmAutomationMessage(t("customers.crm_automation.saved"));
    } catch (e) {
      console.error(e);
      setCrmAutomationMessage(t("customers.crm_automation.load_error"));
    } finally {
      setCrmAutomationSaveLoading(false);
    }
  };

  const isFiltering =
    filters.employeeIds.length > 0 ||
    filters.statuses.length > 0 ||
    filters.withoutUpcomingAppointment ||
    !!selectedWeddingMonthDate;

  const actionButton = (action: FormType) => {
    switch (action) {
      case 'CREATE':
        return <span className="flex justify-center"><PrimaryButton text={t("global.create")!} fn={() => registerAndClose(selectedCustomer!)} /></span>;
      case 'UPDATE':
        return <span className="flex justify-center"><PrimaryButton text={t("global.update")!} fn={() => registerAndClose(selectedCustomer!)} /></span>;
      default:
        return;
    }
  };

  const registerAndClose = async (e: Customer) => {
    if (!!e) {
      if (action === 'CREATE') {
        addFn(e);
      } else {
        updateFn(e);
      }
      setSelectedCustomer(null);
      setAction("READ");
    }
  }

  // const openMeetingForm = (customer: Customer) => {
  //   setShowMeetingForm(false);
  // }

  const viewCustomerDetails = async (customer: Customer) => {
    const enrichedCustomer = await loadEnrichedCustomerOrFallback(customer);
    setSelectedCustomer(enrichedCustomer);
    
    if (canEditCustomers) {
      setAction('UPDATE');
    } else {
      setAction('READ');
    }
  }

  const editCustomer = async (customer: Customer) => {
    const enrichedCustomer = await loadEnrichedCustomerOrFallback(customer);
    setSelectedCustomer(enrichedCustomer);
    setAction('UPDATE');
  }

  const toggleFlagged = async (customer: Customer) => {
    await updateFn({ ...customer, flagged: !customer.flagged }, { reload: false });
  };

  const patchCustomer = async (
    customer: Customer,
    patch: Partial<Customer>,
    successKey: string,
  ) => {
    if (!customer.id) {
      return;
    }
    setSavingCustomerId(String(customer.id));
    const response = await updateFn({ ...customer, ...patch }, { reload: false });
    if (response) {
      toastService.showToast(t(successKey), 'success');
    }
    setSavingCustomerId(null);
  };

  const isCustomerSaving = (customer: Customer) =>
    savingCustomerId != null && String(customer.id) === savingCustomerId;

  const customerColumns: CustomerColumn[] = [
    {
      key: 'name',
      title: t("customers.details.name") || "Name",
      render: (customer) => (
        <div className="flex items-center space-x-2">
          <p 
            className="font-medium cursor-pointer hover:text-blue-600 transition-colors truncate"
            onClick={(e) => {
              e.stopPropagation();
              void viewCustomerDetails(customer);
            }}
          >
            {`${customer.name} ${customer.surname}` || 'Unnamed Customer'}
          </p>
          {customer.flagged && (
            <Tooltip content={t("customers.flagged.tooltip", "Needs review")}>
              <Icon url={highPriorityIcon} cssStyles="w-4" />
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      key: 'employeeId' as keyof Customer,
      title: t("customers.details.assigned_to") || "Assigned to",
      render: (customer) =>
        canEditCustomers ? (
          <CustomerTableAssigneeSelect
            customer={customer}
            employees={employees}
            isSaving={isCustomerSaving(customer)}
            onSave={(employeeId) =>
              patchCustomer(customer, { employeeId }, 'toast.success.customers.assignee_updated')
            }
          />
        ) : (
          <div className="min-w-[10rem] max-w-[14rem]">
            <span className="text-sm text-gray-600 truncate block">
              {customer.employeeId
                ? employeeNameById.get(String(customer.employeeId)) ?? '-'
                : t("customers.details.unassigned")}
            </span>
          </div>
        ),
    },
    {
      key: 'tryOnCount',
      title: t("customers.details.try_on_appointments") || "Try-ons",
      sortable: true,
      render: (customer) => (
        <div className="w-14 text-center">
          <TryOnHeatBadge
            count={countTryOnAppointments(customer.meetings)}
            meetings={customer.meetings}
          />
        </div>
      ),
    },
    {
      key: 'status',
      title: t("customers.details.status") || "Status",
      render: (customer) =>
        canEditCustomers ? (
          <CustomerTableStatusSelect
            customer={customer}
            isSaving={isCustomerSaving(customer)}
            onSave={(status) =>
              patchCustomer(customer, { status }, 'toast.success.customers.status_updated')
            }
          />
        ) : (
          <div className="w-32 text-center">
            <CustomerStatusPill status={customer.status as CustomerStatus} />
          </div>
        ),
    },
    {
      key: 'weddingDate',
      title: t("customers.details.wedding_date") || "Wedding Date",
      sortable: true,
      render: (customer) => {
        let date: Date | null = null;
        if (customer.weddingDate) {
          if (Array.isArray(customer.weddingDate)) {
            date = parseLegacyDateArray(customer.weddingDate);
          } else {
            date = safeParseDate(customer.weddingDate);
          }
        }
        const urgency = date ? formatWeddingUrgency(date) : null;
        return (
          <div className="min-w-[5rem]">
            <span className="text-sm text-gray-600 truncate block">
              {date ? date.toLocaleDateString() : '-'}
            </span>
            {urgency && (
              <span className={`text-xs block ${weddingUrgencyToneClass(urgency.tone)}`}>
                {urgency.params
                  ? t(urgency.labelKey, urgency.params as Record<string, number>)
                  : t(urgency.labelKey)}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'meetings',
      title: t("customers.details.upcoming_appointments") || "Upcoming appointments",
      sortable: true,
      render: (customer) => {
        const upcomingAppointments = getUpcomingAppointments(customer);
        const nextAppointment = upcomingAppointments[0];

        if (!nextAppointment) {
          return <span className="text-sm text-gray-400">-</span>;
        }

        const start = parseDateTime(nextAppointment.startTime);
        const end = parseDateTime(nextAppointment.endTime);
        const additionalAppointments = upcomingAppointments.length - 1;
        const upcomingAppointmentLabels = upcomingAppointments
          .map((appointment, index) => {
            const appointmentStart = parseDateTime(appointment.startTime);
            const appointmentEnd = parseDateTime(appointment.endTime);
            return `${index + 1}. ${appointmentStart.date} ${appointmentStart.time} - ${appointmentEnd.time}`;
          });

        return (
          <div className="min-w-[8rem]">
            <span className="text-sm font-medium text-gray-700 block">
              {start.date}
            </span>
            <span className="text-xs text-gray-500 block">
              {start.time} - {end.time}
            </span>
            {additionalAppointments > 0 && (
              <Tooltip
                content={
                  <div className="space-y-1 text-left">
                    {upcomingAppointmentLabels.map((appointmentLabel) => (
                      <div key={appointmentLabel} className="whitespace-nowrap">
                        {appointmentLabel}
                      </div>
                    ))}
                  </div>
                }
                position="bottom"
                contentClassName="w-max max-w-none"
              >
                <span className="text-xs text-gray-500 block">
                  {t("customers.details.upcoming_appointments_more", "+{{count}} more", { count: additionalAppointments })}
                </span>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      key: 'phone',
      title: t("customers.details.contact") || "Contact",
      render: (customer) => {
        const hasPhone = !!customer.phone;
        const hasEmail = !!customer.email;

        if (!hasPhone && !hasEmail) {
          return <span className="text-sm text-gray-400">-</span>;
        }

        return (
          <div className="max-w-xs min-w-[8rem]">
            <span className="text-sm text-gray-600 truncate block">
              {customer.phone || '-'}
            </span>
            <span className="text-xs text-gray-500 truncate block">
              {customer.email || '-'}
            </span>
          </div>
        );
      },
    },
  ];

  const renderActions = (customer: Customer): React.ReactNode => {
    return (
      <div className="flex justify-center space-x-1">
        {/* <PermissionFlags requiredFlags={['edit-customers'] || []}>
          <IconButton onClick={() => openMeetingForm(customer)}>
            <Icon url={addMeetingIcon} cssStyles="w-5" />
          </IconButton>
        </PermissionFlags> */}
        <PermissionFlags requiredFlags={['edit-customers']}>
          <IconButton actionName="Toggle Customer Flag" onClick={() => toggleFlagged(customer)}>
            <Tooltip content={t("customers.actions.flagged")}>
              <Icon url={customer?.flagged ? highPriorityIcon : lowPriorityIcon} cssStyles="w-5" />
            </Tooltip>
          </IconButton>
        </PermissionFlags>
        <PermissionFlags requiredFlags={['edit-customers']}>
          <IconButton onClick={() => editCustomer(customer)}>
            <Icon url={editIcon} cssStyles="w-5" />
          </IconButton>
        </PermissionFlags>
        <PermissionFlags requiredFlags={['remove-customers']}>
          <IconButton>
            <ConfirmationPopup 
              onConfirm={() => removeFn(customer)} 
              confirmText={t("global.confirm") || "Confirm"} 
              title={`${t("global.remove") || "Remove"}: ${customer.name}`}
            >
              <Icon url={deleteIcon} cssStyles="w-5" />
            </ConfirmationPopup>
          </IconButton>
        </PermissionFlags>
      </div>
    );
  };

  return (
    <PageWrapper>
      <div className="space-y-5">
        <div className="container flex flex-col md:flex-row my-3 items-stretch space-x-0 md:space-x-5">
          <Card>
            <>
              <div className="flex-col">
                <div className="md:flex md:flex-row md:items-center md:space-x-3">
                  <div className="flex items-center space-x-3 mb-2 md:mb-0">
                    <DropSelect
                      compact
                      onSelect={handleWeddingMonthChange}
                      value={selectedWeddingMonthDate}
                      placeholder={t("calendar.meeting.form.wedding_date", "Wedding month") || "Wedding month"}
                    />
                  </div>
                  <div className="container flex flex-wrap space-x-5 mb-2 md:mb-0">
                    <Autocomplete
                      resultsSize={15}
                      searchFn={search}
                      onSelect={(customer: Customer) => void viewCustomerDetails(customer)}
                      placeholder={t("customers.search.placeholder") || "Search customers..."} 
                      isLoading={searchIsLoading}
                      errorMessage={t("global.empty_results") || "No results found"}
                      toElementFn={(customer: Customer) => (
                        <CustomerSearchResult 
                          key={customer.id} 
                          customer={customer} 
                          onView={viewCustomerDetails} 
                        />
                      )}
                    />
                  </div>
                  <div className="hidden md:flex">
                    <SecondaryButton 
                      text={<span className="inline-flex items-center">{t("filters.button", "Filters")}{isFiltering && <span className="ml-1 inline-block w-2 h-2 rounded-full bg-amber-500"/>}</span>} 
                      fn={() => setShowFilters(!showFilters)}
                      icon={<Icon url={funnelIcon} cssStyles="w-4 h-4" />}
                    />
                  </div>
                </div>
                <span className="md:hidden">
                  <SecondaryButton 
                    text={<span className="inline-flex items-center">{t("filters.button", "Filters")}{isFiltering && <span className="ml-1 inline-block w-2 h-2 rounded-full bg-amber-500"/>}</span>} 
                    fn={() => setShowFilters(!showFilters)}
                    icon={<Icon url={funnelIcon} cssStyles="w-4 h-4" />}
                  />
                </span>
              </div>
              {showFilters && (
                <>
                  <div className="space-y-2 mt-2">
                    {/* Employee Section */}
                    <div className="space-y-3">
                      <h3 className="text-sm font-semibold text-gray-700">{t("customers.filters.employees", "Employees")}</h3>
                      <div className="container flex flex-wrap max-h-48 overflow-y-scroll">
                        {
                          employees.length > 0 ? employees.map((s, i) => <FilterPill isActive={filters.employeeIds.includes(String(s.id))} key={i} text={s.name} fn={() => toggleEmployee(s)} />)
                          : (
                            <p className="text-sm text-gray-500">{t("customers.filters.employees.empty", "No employees found with role sales representative")}</p>
                          )
                        }
                      </div>
                    </div>

                    {/* Status Section */}
                    <div className="space-y-3">
                      <h3 className="text-sm font-semibold text-gray-700">{t("customers.filters.status", "Status")}</h3>
                      <div className="container flex flex-wrap max-h-48 overflow-y-scroll">
                        {getCustomerStatuses().map((status, i) => (
                          <FilterPill 
                            key={i}
                            text={t(customerStatusToTranslationKey(status))}
                            isActive={filters.statuses.includes(status)}
                            fn={() => toggleStatus(status)}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              )}
              <div className="mt-3">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">
                  {t("customers.filters.presets", "Quick filters")}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {CUSTOMER_FILTER_PRESET_IDS.map((presetId) => (
                    <FilterPill
                      key={presetId}
                      text={t(customerFilterPresetTranslationKey(presetId))}
                      isActive={resolvedActivePresetId === presetId}
                      fn={() => applyPreset(presetId)}
                    />
                  ))}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-3 mt-3">
                <PermissionFlags requiredFlags={['edit-customers']}>
                  <PrimaryButton
                    text={
                      <span className="flex items-center gap-2">
                        <Plus className="w-4 h-4 shrink-0" />
                        {t("global.create")!}
                      </span>
                    }
                    fn={() => { setAction('CREATE');setSelectedCustomer(EMPTY_CUSSTOMER);}}
                    actionName={t("global.create") || "Create"}
                  />
                </PermissionFlags>
                {canEditCustomers && (
                  <PrimaryButton
                    text={
                      <span className="flex items-center gap-2">
                        <FileText className="w-4 h-4 shrink-0" />
                        {t("customers.crm_automation.configure_advanced")}
                      </span>
                    }
                    fn={() => setShowCrmAutomationSettingsView(true)}
                    actionName={t("customers.crm_automation.configure_advanced") || "Configure Advanced Options"}
                  />
                )}
                {isFiltering && (
                  <SecondaryButton 
                    actionName="Show All Customers" 
                    text={t("filters.reset", "Reset Filters")!} 
                    fn={resetFilters}
                    icon={
                      <span className="relative inline-block w-4 h-4">
                        <Icon url={funnelIcon} cssStyles="w-4 h-4" />
                        <span className="absolute -top-1 -right-1">
                          <Icon url={xMarkIcon} cssStyles="w-3 h-3" />
                        </span>
                      </span>
                    }
                    compact
                  />
                )}
              </div>
            </>
          </Card>
        </div>
        {canEditCustomers && showCrmAutomationSettingsView && (
          <div className="container flex flex-col md:flex-row my-3 items-stretch space-x-0 md:space-x-5">
            <Card>
              <div className="text-sm text-gray-800 max-w-3xl">
                <SecondaryButton
                  actionName={t("customers.crm_automation.back_to_customers") || "Back to customer list"}
                  text={
                    <span className="inline-flex items-center gap-2">
                      <ArrowLeft className="w-4 h-4 shrink-0" />
                      {t("customers.crm_automation.back_to_customers")}
                    </span>
                  }
                  fn={() => {
                    setShowCrmAutomationSettingsView(false);
                    setCrmAutomationMessage(null);
                  }}
                />
                <h2 className="mt-4 text-lg font-semibold text-gray-900">
                  {t("customers.crm_automation.summary")}
                </h2>
                <p className="mt-2 text-gray-600">
                  <Trans
                    i18nKey="customers.crm_automation.intro"
                    components={{
                      closedPill: <CustomerStatusPill status="CLOSED" />,
                      visitPill: <CustomerStatusPill status="VISIT" />,
                    }}
                  />
                </p>
                {crmAutomationLoading && (
                  <p className="mt-2 text-gray-500">{t("global.loading", "Loading…")}</p>
                )}
                {crmAutomationSettings && !crmAutomationLoading && (
                  <div className="mt-4 space-y-4 max-w-xl">
                    <div className="flex items-center justify-between gap-4">
                      <span>{t("customers.crm_automation.master")}</span>
                      <Switch
                        checked={crmAutomationSettings.automationEnabled}
                        onChange={() =>
                          patchCrmAutomation({ automationEnabled: !crmAutomationSettings.automationEnabled })
                        }
                      />
                    </div>
                    <div className="flex items-center justify-between gap-4 opacity-100">
                      <span
                        className={`inline-flex flex-wrap items-center gap-1.5 ${!crmAutomationSettings.automationEnabled ? "text-gray-400" : ""}`}
                      >
                        <Trans
                          i18nKey="customers.crm_automation.sale_closed"
                          components={{
                            closedPill: <CustomerStatusPill status="CLOSED" />,
                          }}
                        />
                      </span>
                      <Switch
                        checked={crmAutomationSettings.autoClosedOnSaleLinked}
                        disabled={!crmAutomationSettings.automationEnabled}
                        onChange={() =>
                          patchCrmAutomation({
                            autoClosedOnSaleLinked: !crmAutomationSettings.autoClosedOnSaleLinked,
                          })
                        }
                      />
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span
                        className={`inline-flex flex-wrap items-center gap-1.5 ${!crmAutomationSettings.automationEnabled ? "text-gray-400" : ""}`}
                      >
                        <Trans
                          i18nKey="customers.crm_automation.first_visit"
                          components={{
                            visitPill: <CustomerStatusPill status="VISIT" />,
                          }}
                        />
                      </span>
                      <Switch
                        checked={crmAutomationSettings.autoVisitOnFirstMeeting}
                        disabled={!crmAutomationSettings.automationEnabled}
                        onChange={() =>
                          patchCrmAutomation({
                            autoVisitOnFirstMeeting: !crmAutomationSettings.autoVisitOnFirstMeeting,
                          })
                        }
                      />
                    </div>
                  </div>
                )}
                {crmAutomationMessage && (
                  <p className="mt-3 text-sm text-amber-800" role="status">
                    {crmAutomationMessage}
                  </p>
                )}
                <div className="mt-4">
                  <PrimaryButton
                    text={t("customers.crm_automation.save")!}
                    fn={() => void saveCrmAutomationSettings()}
                    isDisabled={crmAutomationSaveLoading || !crmAutomationSettings || crmAutomationLoading}
                  />
                </div>
              </div>
            </Card>
          </div>
        )}
        {!showCrmAutomationSettingsView && (
        <Card>
          <>
            <div className="container">
              <Table<CustomerTableRow>
                isLoading={isLoading}
                data={customers}
                columns={customerColumns}
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={handleSort}
                onRowClick={(customer) => void viewCustomerDetails(customer)}
                emptyListComponent={
                  <NoDataCTA
                      headline={t("customers.empty.cta.title")}
                      subtext={t("customers.empty.cta.body")}
                      primaryActionText={t("customers.new_customer.button")}
                      onPrimaryAction={() => navigate('/calendar')}
                  />
                }
                isSelectable={false}
                selectedItems={[]}
                compareFunction={(item, selectedItem) => item.id === selectedItem.id}
                renderActions={renderActions}
              />
            </div>
            {pagination.totalPages > 1 && (
              <div className="flex justify-center mt-4 items-center gap-2">
                <SecondaryButton
                  text={t("customers.pagination.prev", "Prev") as string}
                  fn={() => setPage(Math.max(0, pagination.number - 1))}
                  isDisabled={pagination.number === 0}
                  actionName="Pagination Prev"
                />
                <span className="px-3 py-1 mx-1 text-sm text-gray-600">
                  {t("customers.pagination.page", "Page {{page}} of {{totalPages}}", { 
                    page: pagination.number + 1, 
                    totalPages: pagination.totalPages 
                  })}
                </span>
                <SecondaryButton
                  text={t("customers.pagination.next", "Next") as string}
                  fn={() => setPage(Math.min(pagination.totalPages - 1, pagination.number + 1))}
                  isDisabled={pagination.number >= pagination.totalPages - 1}
                  actionName="Pagination Next"
                />
              </div>
            )}
          </>
        </Card>
        )}
          { selectedCustomer && 
            <Modal modalTitle={selectedCustomer.name || "customers.form.new_customer"} canClose onClose={() => setSelectedCustomer(null)}>
              <CustomerForm customer={selectedCustomer} onCustomerChange={setSelectedCustomer} actionButton={actionButton(action)} isDisabled={action === 'READ'}/>
            </Modal>
          }
          {/* TODO: add Customer info and logic to MeetingForm */}
          { showMeetingForm && 
            <Modal modalTitle={selectedCustomer?.name || "customers.form.new_customer"} canClose onClose={() => {setSelectedCustomer(null); setShowMeetingForm(false);}}>
              <MeetingForm meeting={EMPTY_MEETING} onMeetingChange={() => null} actionButton={actionButton('CREATE')}/>
            </Modal>
          }
      </div>
    </PageWrapper>
  );
}

export default CustomersPage;
